// src/components/admin_panel/AdminPaymentIssues.js
import React, { useState } from "react";
import { adminApi } from "../../services/adminApi";
import AnalyzingIndicator from "./AnalyzingIndicator";

const CURRENCY_SYMBOLS = { gbp: "£", usd: "$", eur: "€" };

function formatAmount(amount, currency) {
  if (amount == null) return "—";
  const symbol = CURRENCY_SYMBOLS[(currency || "").toLowerCase()] || `${(currency || "").toUpperCase()} `;
  return `${symbol}${(amount / 100).toFixed(2)}`;
}

function formatDate(unixSeconds) {
  if (!unixSeconds) return "—";
  const date = new Date(unixSeconds * 1000);
  if (isNaN(date.getTime())) return "—";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function riskLevelStyle(riskLevel) {
  if (riskLevel === "highest") return { background: "#fee2e2", color: "#991b1b", fontWeight: "600" };
  if (riskLevel === "elevated") return { background: "#fffbeb", color: "#92400e", fontWeight: "600" };
  return {};
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: "#f8f9fa", borderRadius: "8px", padding: "12px 16px", minWidth: "140px",
    }}>
      <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{label}</div>
      <div style={{ fontSize: "1.5rem", fontWeight: "700", color: color || "#111827" }}>{value}</div>
    </div>
  );
}

const DAYS_OPTIONS = [1, 7, 14, 30, 90];

function AdminPaymentIssues({ adminToken, tabName }) {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [emailSearch, setEmailSearch] = useState("");
  const [kindFilter, setKindFilter] = useState(""); // "", "failed", "risky"

  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [analysis, setAnalysis] = useState(null);

  async function load() {
    if (!adminToken) return;
    setLoading(true);
    setError("");
    setAnalysis(null);
    setAnalysisError("");
    try {
      const result = await adminApi.getPaymentIssues(adminToken, days);
      setData(result);
    } catch (err) {
      console.error("Error fetching payment issues:", err);
      setError(err.message || "Failed to load payment issues");
    } finally {
      setLoading(false);
    }
  }

  async function analyze() {
    if (!adminToken) return;
    setAnalyzing(true);
    setAnalysisError("");
    try {
      const result = await adminApi.analyzePaymentIssues(adminToken, days);
      // The endpoint does its own fresh Stripe scan alongside the Claude
      // call, so this one click also populates the stats/table below -
      // no need to click Load first or afterward.
      setData(result);
      setAnalysis(result.analysis);
    } catch (err) {
      console.error("Error analyzing payment issues:", err);
      setAnalysisError(err.message || "Failed to analyze payment issues");
    } finally {
      setAnalyzing(false);
    }
  }

  const items = data?.items || [];
  const filtered = items.filter((c) => {
    if (emailSearch && !(c.customer_email || "").toLowerCase().includes(emailSearch.toLowerCase())) {
      return false;
    }
    const isRisky = c.risk_level === "elevated" || c.risk_level === "highest";
    if (kindFilter === "failed" && c.status !== "failed") return false;
    if (kindFilter === "risky" && !isRisky) return false;
    return true;
  });

  return (
    <div>
      <h3 style={{ textAlign: "center" }}>{tabName}</h3>
      <p style={{ textAlign: "center", color: "#6b7280", fontSize: "0.9rem", marginTop: "-0.5rem" }}>
        Scans recent Stripe charges directly from the Stripe API for outright failures
        and charges Stripe Radar flagged as elevated or highest risk. Both buttons below
        hit the Stripe API live, so nothing loads automatically on tab open.
      </p>

      <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", justifyContent: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
        <label style={{ fontSize: "0.85rem", color: "#374151" }}>
          Lookback window
          <br />
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ padding: "6px", borderRadius: "6px", border: "1px solid #d1d5db" }}>
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>Last {d} day{d === 1 ? "" : "s"}</option>
            ))}
          </select>
        </label>
        <button
          onClick={load}
          disabled={loading}
          style={{
            background: loading ? "#a5b4fc" : "#f3f4f6", color: loading ? "white" : "#374151",
            border: "1px solid #d1d5db",
            padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Scanning Stripe..." : "Load"}
        </button>
        <button
          onClick={analyze}
          disabled={analyzing}
          style={{
            background: analyzing ? "#a5b4fc" : "#667eea", color: "white", border: "none",
            padding: "8px 16px", borderRadius: "8px", fontSize: "13px", fontWeight: "600",
            cursor: analyzing ? "not-allowed" : "pointer",
          }}
        >
          {analyzing ? "Analyzing with AI..." : "Analyze with AI"}
        </button>
      </div>

      <p style={{ textAlign: "center", fontSize: "0.75rem", color: "#9ca3af", marginTop: 0, marginBottom: "1rem" }}>
        Load just fetches and lists the charges. Analyze with AI pulls a fresh scan and sends an
        anonymized summary (no emails, names, or card details) to the Anthropic API for analysis.
      </p>

      {analyzing && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <AnalyzingIndicator color="#667eea" />
        </div>
      )}

      {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
      {analysisError && <p style={{ color: "red", textAlign: "center" }}>{analysisError}</p>}

      {analysis && (
        <div style={{
          textAlign: "left", background: "#f8f9fa", borderRadius: "8px", padding: "16px 20px",
          marginBottom: "1.5rem", whiteSpace: "pre-wrap", lineHeight: "1.5", fontSize: "0.9rem",
        }}>
          {analysis}
        </div>
      )}

      {data && (
        <>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", marginBottom: "1rem", flexWrap: "wrap" }}>
            <StatCard label="Failed charges" value={data.failed_count} color="#991b1b" />
            <StatCard label="High-risk charges" value={data.risky_count} color="#92400e" />
            <StatCard label="Charges scanned" value={data.scanned_count} />
          </div>

          {data.truncated && (
            <p style={{ textAlign: "center", color: "#92400e", fontSize: "0.85rem" }}>
              Stopped after scanning {data.scanned_count} charges, there may be more in this window.
              Try a shorter lookback window for a complete scan.
            </p>
          )}

          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
            <input
              type="text"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              placeholder="Search customer email"
              style={{ width: "220px", padding: "6px", borderRadius: "6px", border: "1px solid #d1d5db" }}
            />
            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} style={{ padding: "6px", borderRadius: "6px", border: "1px solid #d1d5db" }}>
              <option value="">Failed or high-risk</option>
              <option value="failed">Failed only</option>
              <option value="risky">High-risk only</option>
            </select>
          </div>

          {filtered.length === 0 ? (
            <p style={{ textAlign: "center" }}>No failed or high-risk charges in this window.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #ccc", background: "#f0f0f0" }}>
                  <th style={{ textAlign: "left", padding: "8px" }}>Date</th>
                  <th style={{ textAlign: "left", padding: "8px" }}>Customer</th>
                  <th style={{ textAlign: "left", padding: "8px" }}>Amount</th>
                  <th style={{ textAlign: "left", padding: "8px" }}>Status</th>
                  <th style={{ textAlign: "left", padding: "8px" }}>Failure reason</th>
                  <th style={{ textAlign: "left", padding: "8px" }}>Radar risk</th>
                  <th style={{ textAlign: "left", padding: "8px" }}>Card</th>
                  <th style={{ textAlign: "left", padding: "8px" }}>Stripe</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, idx) => (
                  <tr
                    key={c.id}
                    style={{
                      backgroundColor: idx % 2 === 0 ? "#fff" : "#f9f9f9",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <td style={{ padding: "8px" }}>{formatDate(c.created)}</td>
                    <td style={{ padding: "8px" }}>{c.customer_email || c.customer_name || c.customer_id || "—"}</td>
                    <td style={{ padding: "8px" }}>{formatAmount(c.amount, c.currency)}</td>
                    <td style={{ padding: "8px", textTransform: "capitalize" }}>{c.status || "—"}</td>
                    <td style={{ padding: "8px" }}>
                      {c.failure_code ? `${c.failure_code}${c.failure_message ? `: ${c.failure_message}` : ""}` : "—"}
                    </td>
                    <td style={{ padding: "8px", ...riskLevelStyle(c.risk_level) }}>
                      {c.risk_level ? `${c.risk_level}${c.risk_score != null ? ` (${c.risk_score})` : ""}` : "—"}
                    </td>
                    <td style={{ padding: "8px", textTransform: "capitalize" }}>
                      {c.card_brand ? `${c.card_brand} ****${c.card_last4 || ""}` : "—"}
                    </td>
                    <td style={{ padding: "8px" }}>
                      <a href={c.dashboard_url} target="_blank" rel="noreferrer">View</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default AdminPaymentIssues;
